import { Fragment, useEffect, useRef, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { WidgetIcons } from "./icons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EhrChip } from "@/components/chat/SidebarChat/DocLink/EhrChip";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { UserAvatar } from "@/components/ui/user-avatar";
import { useSaveFieldValue } from "@/hooks/buildings/useSaveFieldValue";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { BuildingDataWidgetSections } from "@/lib/i18n/types/buildings-sections/building-data-widget";
import type {
  FieldState,
  WidgetField,
  WidgetFieldSource,
} from "@/domains/buildings/types/buildingWidget";

import { EnumMultiSelect, type EnumOption } from "./EnumMultiSelect";
import { CLEAR_SENTINEL } from "./enumValue";
import { SourceHoverCard } from "./SourceHoverCard";
import { ehrUrlFor } from "./ehrUrl";
import { getEnumRule } from "./getEnumRule";
import { groupNonUserSources, groupSourcesByValue } from "./sourceGroups";
import { sourceReference } from "./sourceReference";
import { SourceGroupChip } from "./SourceGroupChip";
import { AttributeHistoryModal } from "./AttributeHistoryModal";
import { shouldCommitMultiSelect } from "./multiSelectCommit";
import { visibleTooltipSources } from "./visibleTooltipSources";
import { stripTrailingUnit } from "./stripTrailingUnit";
import { validateFieldValue } from "./validateFieldValue";
import { FieldControls, IconButton } from "./FieldRowControls";
import { hasFieldControls } from "./fieldControlState";
import { isRedundantUserSave } from "./isRedundantSave";

interface FieldRowProps {
  field: WidgetField;
  /** Registry code of the currently-viewed building (for the EHR deep-link). */
  registryCode?: string | null;
  /** Opens the app-level document preview for a given filename. */
  onDocPreview: (filename: string) => void;
  /** Supabase org id — required to save user edits. */
  orgId: string | undefined;
  /** Stringified building id — required to save user edits. */
  buildingId: string | undefined;
  /**
   * When false the field is read-only: no edit mode, no confirm/clear
   * controls, no conflict resolution. Gated on MANAGE_BUILDING_DATA_SIDEBAR
   * (WHA-3488).
   */
  canManage: boolean;
  /** Opens or repopulates the page inspector for this field. */
  onSelect?: (field: WidgetField) => void;
  /** Draws the selected-row affordance without changing data behaviour. */
  selected?: boolean;
}

const REVIEW_BADGE_CLASS =
  "whitespace-nowrap rounded-full bg-warning/20 px-2 py-0.5 text-[11px] font-medium text-warning-900";

/**
 * The row's right-hand badge. Two things wear it: a field that still needs
 * review, and a confirmed field a newer extractor finding now disagrees with.
 * Nothing else does — a settled value and a missing one both read from the row
 * itself, as the value or as the "Add value" prompt standing in for it.
 *
 * The second used to be a 7px `--bd-warn` dot, which resolves to the
 * *destructive* colour (`index.css`, `.building-data-widget`) — so the one
 * affordance that meant "there is something to look at here" was the only one
 * painted red, in a page whose whole review vocabulary is amber. It is the same
 * kind of work, so it gets the same badge, and it carries the disclosure for
 * the Current / New-finding panel that the dot used to open.
 */
function StatusMarker({
  state,
  label,
  proposal,
}: {
  state: FieldState;
  label: string;
  /** Present only for a confirmed field with a live proposal. */
  proposal?: { expanded: boolean; onToggle: () => void };
}) {
  if (proposal) {
    return (
      <button
        type="button"
        aria-expanded={proposal.expanded}
        // The row itself opens the attribute panel; this badge has its own job.
        onClick={(event) => {
          event.stopPropagation();
          proposal.onToggle();
        }}
        className={`${REVIEW_BADGE_CLASS} inline-flex items-center gap-1 hover:bg-warning/30`}
      >
        {label}
        <ChevronDown
          size={11}
          style={{
            transform: proposal.expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 160ms",
          }}
        />
      </button>
    );
  }
  if (state !== "pending") return null;
  return <span className={REVIEW_BADGE_CLASS}>{label}</span>;
}

/**
 * The documents behind a row's value: the first one named, the rest counted.
 * A click opens the named file when it stands alone, or the list when several
 * agree — a hover card would leave the others unreachable on a tablet, and the
 * row has no width to spell out three filenames.
 */
function RowSourceDocs(props: { filenames: string[]; onDocPreview: (filename: string) => void }) {
  const { filenames, onDocPreview } = props;
  const [open, setOpen] = useState(false);
  const [first] = filenames;
  const extraCount = filenames.length - 1;
  const chip = (
    <button
      type="button"
      onClick={extraCount > 0 ? undefined : () => onDocPreview(first)}
      title={extraCount > 0 ? filenames.join("\n") : first}
      className="flex min-w-0 items-center gap-1.5 rounded text-left transition-colors hover:bg-accent/60"
    >
      <FileTypeIcon filename={first} className="h-5 w-5" />
      <span className="min-w-0 truncate text-[11px] text-muted-foreground">{first}</span>
      {extraCount > 0 ? (
        <span className="shrink-0 rounded bg-muted px-1 text-[10px] font-medium text-muted-foreground">
          +{extraCount}
        </span>
      ) : null}
    </button>
  );
  if (extraCount === 0) return chip;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{chip}</PopoverTrigger>
      <PopoverContent align="start" side="bottom" className="w-72 p-1">
        {filenames.map((filename) => (
          <button
            key={filename}
            type="button"
            onClick={() => {
              onDocPreview(filename);
              setOpen(false);
            }}
            className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-xs hover:bg-accent/60"
          >
            <FileTypeIcon filename={filename} className="h-5 w-5" />
            <span className="min-w-0 flex-1 whitespace-normal break-all">{filename}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function persistedMultiSelectValue(picks: string[], state: FieldState): string | null | undefined {
  if (picks.length > 0) return picks.join(",");
  if (state === "confirmed") return "";
  if (state === "pending") return null;
  return undefined;
}

/**
 * Redesigned source tooltip (WHA-3720, Design A). Title bar with the attribute
 * name + a clock button that opens the value-history modal; the winning value
 * (single text or wrapped chips for multi-select enums); then the confirmer row
 * and the stacked supporting-source chips. Rendered inside a portaled
 * HoverCardContent that carries the `building-data-widget` class so the `--bd-*`
 * theme tokens resolve.
 */
function TooltipSourceList({
  label,
  displayValue,
  unit,
  sources,
  currentValue,
  isMultiSelect,
  registryCode,
  onDocPreview,
  onOpenHistory,
  tw,
}: {
  /** Attribute display name shown bold in the title bar. */
  label: string;
  /** Locale-aware winning value (enum labels already resolved). */
  displayValue: string;
  unit: string | null;
  sources: WidgetFieldSource[];
  /**
   * The field's current value. EHR / extractor sources whose value no longer
   * matches it are hidden: once the user changes a value, the supporting files
   * that backed the *previous* value must not keep appearing here (WHA-3641).
   * Match is case- and whitespace-insensitive via `normalizeAttributeValue`.
   * User entries always show regardless of value.
   */
  currentValue: string;
  /** Enables segment-aware matching for comma-joined multi-select values. */
  isMultiSelect: boolean;
  registryCode: string | null | undefined;
  onDocPreview: (filename: string) => void;
  /** Opens the per-attribute value-history modal. */
  onOpenHistory: () => void;
  tw: BuildingDataWidgetSections;
}) {
  const visibleSources = visibleTooltipSources(sources, currentValue, isMultiSelect);
  const userEntries = visibleSources.filter(
    (s): s is Extract<WidgetFieldSource, { kind: "user" }> => s.kind === "user"
  );
  const groups = groupNonUserSources(visibleSources);
  const valueSegments = isMultiSelect
    ? displayValue
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 220, maxWidth: 320 }}>
      {/* Title bar: attribute name + history clock. */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <div
          style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: "var(--bd-ink)" }}
        >
          {label}
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpenHistory();
          }}
          title={tw.viewHistory}
          aria-label={tw.viewHistory}
          style={{
            flex: "0 0 auto",
            display: "grid",
            placeItems: "center",
            width: 22,
            height: 22,
            border: "none",
            background: "transparent",
            color: "var(--bd-ink-3)",
            cursor: "pointer",
            padding: 0,
          }}
        >
          <WidgetIcons.clock size={15} />
        </button>
      </div>

      {/* Winning value — chips for multi-select, plain text otherwise. */}
      {valueSegments.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {valueSegments.map((seg, i) => (
            <span
              key={`${seg}-${i}`}
              style={{
                fontSize: 12,
                padding: "2px 8px",
                borderRadius: 999,
                background: "var(--bd-bg)",
                border: "1px solid var(--bd-line)",
                color: "var(--bd-ink)",
              }}
            >
              {seg}
            </span>
          ))}
        </div>
      ) : (
        <div
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: "var(--bd-ink)",
            overflowWrap: "anywhere",
          }}
        >
          {displayValue || "—"}
          {unit ? (
            <span style={{ marginLeft: 4, fontSize: 11, color: "var(--bd-ink-3)" }}>{unit}</span>
          ) : null}
        </div>
      )}

      {/* Confirmer + supporting sources. */}
      {(userEntries.length > 0 || groups.length > 0) && (
        <div style={{ height: 1, background: "var(--bd-line)" }} />
      )}
      {userEntries.map((src, idx) => {
        const label = src.confirmedBySource ? tw.confirmedBy : tw.editedBy;
        const name = src.editedByName || tw.unknownUser;
        const avatarUser = src.editedById
          ? {
              id: src.editedById,
              email: name,
              first_name: name.split(" ")[0] ?? null,
              last_name: name.split(" ").slice(1).join(" ") || null,
              avatar_url: src.editedByAvatarUrl,
              color: src.editedByColor,
            }
          : null;
        return (
          <div
            key={`user-${idx}`}
            style={{
              fontSize: 11,
              color: "var(--bd-ink-3)",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {avatarUser ? <UserAvatar user={avatarUser} size={14} /> : null}
            <span>
              {label}: {name}
            </span>
          </div>
        );
      })}
      {groups.length > 0 && (
        <>
          <div
            style={{
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontSize: 10,
              color: "var(--bd-ink-4)",
            }}
          >
            {tw.supportingSources}
          </div>
          {groups.map((g, idx) => (
            <div key={`${g.kind}-${idx}`} style={{ display: "flex" }}>
              <SourceGroupChip
                group={g}
                registryCode={registryCode}
                insideHoverCard
                onDocPreview={onDocPreview}
              />
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function InlineValueInput({
  initial,
  unit,
  onSubmit,
  onCancel,
}: {
  initial: string;
  /** Localized unit suffix shown inside the input while typing (WHA-3137). */
  unit: string | null;
  onSubmit: (typed: string) => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState(initial);
  const ref = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  const submit = (raw: string) => {
    // Defensively drop a trailing unit if the user typed one — the platform
    // appends it at display time, so persisting it would double up.
    onSubmit(unit ? stripTrailingUnit(raw) : raw);
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: 28,
      }}
    >
      <input
        ref={ref}
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit(typed);
          } else if (e.key === "Escape") {
            e.preventDefault();
            onCancel();
          }
        }}
        onBlur={() => submit(typed)}
        style={{
          fontSize: 13,
          color: "var(--bd-ink)",
          textAlign: "left",
          border: "1px solid var(--bd-line-strong, var(--bd-line))",
          borderRadius: 4,
          // Reserve space on the right for the unit overlay so the typed
          // number doesn't collide with the suffix.
          padding: unit ? "0 32px 0 8px" : "0 8px",
          width: "100%",
          height: 28,
          boxSizing: "border-box",
          outline: "none",
        }}
      />
      {unit ? (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 11,
            color: "var(--bd-ink-3)",
            pointerEvents: "none",
            userSelect: "none",
            fontFamily: "inherit",
          }}
        >
          {unit}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Layout + click-affordance wrapper for a row's display value. Deliberately
 * borderless and transparent: display states read as plain text so rows
 * don't look "boxed in" (borders belong to the actual form controls shown
 * while editing). Keyboard/click affordances are retained.
 */
function ValueBox({ onClick, children }: { onClick?: () => void; children: React.ReactNode }) {
  const interactive = Boolean(onClick);
  return (
    <div
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      style={{
        width: "100%",
        minHeight: 28,
        boxSizing: "border-box",
        padding: "4px 0",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 4,
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        cursor: interactive ? "pointer" : "default",
      }}
    >
      {children}
    </div>
  );
}

/**
 * Stateful wrapper around `EnumMultiSelect` so the user can toggle several
 * options without each toggle firing a save (which would close the popover
 * via the `editing` state and lose subsequent picks). Selections live in
 * local state for the lifetime of the edit session; we only call `onCommit`
 * once when the popover closes, and only if the selection actually changed.
 */
function EnumMultiSelectField({
  options,
  initialSelected,
  commitEvenIfUnchanged,
  onCommit,
  onCancel,
  placeholder,
  emptyLabel,
  doneLabel,
  cancelLabel,
}: {
  options: EnumOption[];
  initialSelected: string[];
  /**
   * When true (the field has no confirmed user value yet — i.e. pending/empty),
   * clicking Done persists even when the selection equals the pre-filled value.
   * Confirming a pending finding *is* the write; otherwise it silently no-ops.
   */
  commitEvenIfUnchanged: boolean;
  onCommit: (selected: string[]) => void;
  onCancel: () => void;
  placeholder: string;
  emptyLabel: string;
  doneLabel: string;
  cancelLabel: string;
}) {
  const [selected, setSelected] = useState<string[]>(initialSelected);
  // Snapshot the canonical comma-string of the initial set so order changes
  // don't read as edits.
  const initialKey = useRef([...initialSelected].sort().join(",")).current;
  const handledRef = useRef(false);

  const handleClose = (committed: boolean) => {
    if (handledRef.current) return;
    handledRef.current = true;
    if (!committed) {
      onCancel();
      return;
    }
    const currentKey = [...selected].sort().join(",");
    if (
      shouldCommitMultiSelect({
        currentKey,
        initialKey,
        commitEvenIfUnchanged,
        hasSelection: selected.length > 0,
      })
    ) {
      onCommit(selected);
    } else {
      onCancel();
    }
  };

  return (
    <EnumMultiSelect
      options={options}
      selected={selected}
      onChange={setSelected}
      onClose={handleClose}
      placeholder={placeholder}
      emptyLabel={emptyLabel}
      doneLabel={doneLabel}
      cancelLabel={cancelLabel}
    />
  );
}

function ValueText({ value, unit }: { value: string; unit: string | null }) {
  return (
    <span
      style={{
        fontSize: 13,
        color: "var(--bd-ink)",
        fontWeight: 400,
        minWidth: 0,
        textAlign: "right",
        lineHeight: 1.35,
      }}
    >
      {value}
      {unit ? (
        <span
          style={{
            marginLeft: 4,
            fontSize: 11,
            color: "var(--bd-ink-3)",
            fontFamily: "inherit",
          }}
        >
          {unit}
        </span>
      ) : null}
    </span>
  );
}

/**
 * The value as it reads at rest, before anything is clicked. Exported for unit
 * testing.
 */
export function RestingValue({
  value,
  unit,
  isMultiSelect,
  expanded,
  onToggle,
  showMoreLabel,
  showLessLabel,
}: {
  value: string;
  unit: string | null;
  isMultiSelect: boolean;
  expanded: boolean;
  onToggle: () => void;
  showMoreLabel: string;
  showLessLabel: string;
}) {
  const values = isMultiSelect
    ? value
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean)
    : [];
  // Bordered chips boxed these rows in next to their plain-text neighbours.
  // Dot-separated text keeps each value readable as a discrete item while
  // sitting in the same typography as every single-value row.
  if (values.length > 1) {
    return (
      <span className="min-w-0 text-left text-[13px] leading-5 text-foreground">
        {values.map((part, index) => (
          <Fragment key={`${part}-${index}`}>
            {index > 0 ? (
              <span aria-hidden className="mx-1.5 text-muted-foreground">
                ·
              </span>
            ) : null}
            {part}
          </Fragment>
        ))}
      </span>
    );
  }

  const long = value.length > 90;
  return (
    <span className="min-w-0 text-left text-[13px] leading-5 text-foreground [font-variant-numeric:tabular-nums]">
      <span
        className={expanded || !long ? undefined : "line-clamp-2"}
        style={{ overflowWrap: "anywhere" }}
      >
        {value}
        {unit ? <span className="ml-1 text-[11px] text-muted-foreground">{unit}</span> : null}
      </span>
      {long ? (
        <button
          type="button"
          className="mt-0.5 text-xs font-medium text-muted-foreground hover:text-foreground"
          onClick={(event) => {
            event.stopPropagation();
            onToggle();
          }}
        >
          {expanded ? showLessLabel : showMoreLabel}
        </button>
      ) : null}
    </span>
  );
}

export function FieldRow(props: FieldRowProps) {
  const {
    field,
    registryCode,
    onDocPreview,
    orgId,
    buildingId,
    canManage,
    onSelect,
    selected = false,
  } = props;
  const [hover, setHover] = useState(false);
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [customValue, setCustomValue] = useState("");
  /**
   * When the user types a value that fails range / length / pattern validation,
   * we hold it here and show the warn-and-confirm pill instead of saving
   * immediately. Cleared on Edit, on Save anyway, or on cancel.
   */
  const [pendingExtremeValue, setPendingExtremeValue] = useState<string | null>(null);
  // WHA-3720: opens the per-attribute value-history modal. Held at the row level
  // (not inside the HoverCard) so the modal survives the tooltip closing on hover-out.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [valueExpanded, setValueExpanded] = useState(false);
  const { label, value, state, hasConflict, sources, unit, proposedChange } = field;
  const proposalActive = state === "confirmed" && !editing && !!proposedChange;
  const { t, language } = useLanguage();
  const tw = t.buildings.buildingDataWidget;

  const enumRule = getEnumRule(field.validation, language);
  const isMultiSelect = enumRule?.kind === "enum_multiselect";

  const saveMutation = useSaveFieldValue(buildingId, orgId);
  const isSaving = saveMutation.isPending;

  // One option per distinct value, each carrying every source that reported
  // it — a PDF's "9850" and an IFC model's "9850.0" are one option.
  const valueOptions = state === "pending" ? groupSourcesByValue(sources) : [];

  const reference = sourceReference(field);

  const padV = 11;
  const gap = 12;

  // Reset every in-flight edit affordance back to the row's resting display.
  // Shared by the save success/error paths so a row never lingers mid-edit.
  const resetEditState = () => {
    setEditing(false);
    setExpanded(false);
    setCustomValue("");
    setPendingExtremeValue(null);
  };

  const persistValue = (
    attributeValue: string | null,
    previousValue: string | null = state === "confirmed" ? value : ""
  ) => {
    // Hard gate: a user without MANAGE_BUILDING_DATA_SIDEBAR can never write,
    // regardless of which control reached here. The edit affordances are also
    // hidden below, but this is the single enforced sink.
    if (!canManage) return;
    // `data.user_data` is append-only and doubles as the history table, so a
    // write that changes nothing still shows up as an edit in the attribute
    // timeline and the building-wide modal. Leaving edit mode is the whole
    // response to "the user clicked in and clicked out again".
    if (isRedundantUserSave(field, attributeValue)) {
      resetEditState();
      return;
    }
    saveMutation.mutate(
      {
        attributeKey: field.id,
        attributeValue,
        previousValue,
        offerUndo: true,
      },
      {
        onSuccess: resetEditState,
        // WHA-3643: also leave edit mode when the save fails. Without this a
        // failed multi-select save left the row stuck rendering the (closed)
        // EnumMultiSelect combobox — a bordered control where the confirmed
        // value should read as borderless text. The hook already toasts the
        // error; here we just return the row to its resting display so the
        // last-good value shows borderless like every other confirmed field.
        onError: resetEditState,
      }
    );
  };

  const commitValue = (v: string) => {
    // Defensively drop a trailing unit on numeric fields — covers the
    // typed-value path inside the conflict-resolution UI where the user
    // might still include the unit (WHA-3137).
    const cleaned = unit ? stripTrailingUnit(v) : v.trim();
    if (!cleaned) {
      setEditing(false);
      setPendingExtremeValue(null);
      return;
    }
    // Enum-typed values come from a fixed list — the dropdown guarantees
    // membership, so skip the range/length/pattern check entirely.
    if (!enumRule) {
      const result = validateFieldValue(cleaned, field.validation);
      if (!result.ok) {
        setPendingExtremeValue(cleaned);
        return;
      }
    }
    persistValue(cleaned);
  };

  // SQL NULL records a pending rejection without exposing an app-private value
  // to other readers. The historic blank string removes a confirmed override
  // and reveals its machine findings again.
  const clearValue = () => {
    persistValue(state === "confirmed" ? "" : null);
  };

  // True when the saved value can't be repaired through the regular dropdown.
  // For single enum: the value isn't one of the options. For multiselect: ALL
  // segments are unknown — partial cases (one bad value next to several good
  // ones) are handled inside the popover itself, where extras render as
  // checkbox items the user can uncheck individually. The standalone clear
  // here is the all-or-nothing fallback for the truly stuck case.
  const valueOutsideEnum = (() => {
    if (!enumRule || !value) return false;
    if (enumRule.kind === "enum_multiselect") {
      const segs = value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (segs.length === 0) return false;
      const known = new Set(enumRule.options.map((o) => o.value));
      return segs.every((s) => !known.has(s));
    }
    return !enumRule.options.some((o) => o.value === value);
  })();

  let valueNode: React.ReactNode;

  // Resolve the canonical English value to its locale-aware label so confirmed
  // / pending enum values aren't shown as "No" while the rest of the UI is in
  // Estonian. Falls back to the raw value if no option matches (extractor can
  // emit free-form strings that don't map cleanly to the enum).
  const displayValue: string = (() => {
    if (!enumRule || !value) return value;
    if (enumRule.kind === "enum_multiselect") {
      return value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
        .map((v) => enumRule.options.find((o) => o.value === v)?.label ?? v)
        .join(", ");
    }
    return enumRule.options.find((o) => o.value === value)?.label ?? value;
  })();

  // Same locale-aware resolution for the extractor's proposed value, so the
  // diff doesn't show an enum's canonical key while the rest of the UI is in
  // the user's language (WHA-3291).
  const proposedDisplayValue: string = (() => {
    if (!proposedChange) return "";
    const raw = proposedChange.value;
    if (!enumRule || !raw) return raw;
    if (enumRule.kind === "enum_multiselect") {
      return raw
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
        .map((v) => enumRule.options.find((o) => o.value === v)?.label ?? v)
        .join(", ");
    }
    return enumRule.options.find((o) => o.value === raw)?.label ?? raw;
  })();

  if (editing) {
    if (enumRule?.kind === "enum") {
      // Saved value isn't one of the enum options — likely a free-form
      // value from EHR / extractor. Surface it as a regular SelectItem at
      // the end of the list (in place of the Clear-value item) so the user
      // can replace it by picking a real option. Once replaced, it won't
      // come back on the next open. Same shape as the multi-select extras.
      const valueIsExtra = !!value && !enumRule.options.some((o) => o.value === value);
      valueNode = (
        <Select
          defaultValue={value || undefined}
          defaultOpen
          onValueChange={(picked) => {
            if (picked === CLEAR_SENTINEL) {
              clearValue();
              return;
            }
            commitValue(picked);
          }}
          onOpenChange={(open) => {
            if (!open && !saveMutation.isPending) setEditing(false);
          }}
        >
          <SelectTrigger className="h-7 px-2 text-xs">
            <SelectValue placeholder={tw.selectAnOption} />
          </SelectTrigger>
          <SelectContent>
            {enumRule.options.length === 0 && !valueIsExtra ? (
              <div className="px-2 py-1 text-xs text-muted-foreground">{tw.noOptions}</div>
            ) : (
              <>
                {enumRule.options.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
                {valueIsExtra ? (
                  <SelectItem key={`extra-${value}`} value={value}>
                    {value}
                  </SelectItem>
                ) : value ? (
                  <SelectItem value={CLEAR_SENTINEL} className="text-muted-foreground">
                    {tw.clearValue}
                  </SelectItem>
                ) : null}
              </>
            )}
          </SelectContent>
        </Select>
      );
    } else if (enumRule?.kind === "enum_multiselect") {
      const initialSelected = value
        ? value
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
      valueNode = (
        <EnumMultiSelectField
          options={enumRule.options}
          initialSelected={initialSelected}
          commitEvenIfUnchanged={state !== "confirmed"}
          onCommit={(picks) => {
            const persisted = persistedMultiSelectValue(picks, state);
            if (persisted === undefined) {
              resetEditState();
              return;
            }
            persistValue(persisted);
          }}
          onCancel={() => setEditing(false)}
          placeholder={tw.selectMultiple}
          emptyLabel={tw.noOptions}
          doneLabel={tw.done}
          cancelLabel={tw.cancel}
        />
      );
    } else {
      valueNode = (
        <InlineValueInput
          initial={value}
          unit={unit}
          onSubmit={(typed) => commitValue(typed)}
          onCancel={() => {
            setEditing(false);
            setPendingExtremeValue(null);
          }}
        />
      );
    }
  } else if (state === "empty") {
    // Quiet text affordance instead of a dashed box: pencil + "Add value",
    // muted until the row is hovered.
    valueNode = canManage ? (
      <ValueBox onClick={() => setEditing(true)}>
        <span
          aria-label={tw.addValue}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            fontSize: 12,
            color: hover ? "var(--bd-ink-2)" : "var(--bd-ink-4)",
            transition: "color 120ms",
          }}
        >
          <WidgetIcons.edit size={12} />
          {tw.addValue}
        </span>
      </ValueBox>
    ) : (
      <ValueBox>
        <span style={{ color: "var(--bd-ink-4)", fontSize: 12 }}>—</span>
      </ValueBox>
    );
  } else if (state === "pending" && hasConflict) {
    valueNode = (
      <ValueBox onClick={() => setExpanded((v) => !v)}>
        <span
          style={{
            color: "var(--bd-warn)",
            fontSize: 12,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            textDecoration: hover ? "underline" : "none",
          }}
        >
          {tw.optionsCount.replace("{count}", String(valueOptions.length))}
          <ChevronDown
            size={12}
            style={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 160ms",
            }}
          />
        </span>
      </ValueBox>
    );
  } else if (state === "confirmed") {
    // Confirmed, with or without a newer finding waiting behind the row's
    // "New finding" badge. WHA-3290: no border — a settled value reads as plain
    // text. The value is NOT the edit trigger: a click anywhere on the row
    // opens the attribute panel, and the hover pencil is what starts an inline
    // edit. A proposal used to render its own right-aligned wrapper here, which
    // pushed exactly those rows out of line with their neighbours; sharing this
    // branch is what keeps every confirmed value on the same left edge.
    valueNode = (
      <SourceHoverCard
        triggerStyle={{ width: "100%" }}
        trigger={
          <ValueBox>
            <RestingValue
              value={displayValue}
              unit={unit}
              isMultiSelect={isMultiSelect}
              expanded={valueExpanded}
              onToggle={() => setValueExpanded((current) => !current)}
              showMoreLabel={tw.showMore}
              showLessLabel={tw.showLess}
            />
          </ValueBox>
        }
      >
        <TooltipSourceList
          label={label}
          displayValue={displayValue}
          unit={unit}
          sources={sources}
          currentValue={value}
          isMultiSelect={isMultiSelect}
          registryCode={registryCode}
          onDocPreview={onDocPreview}
          onOpenHistory={() => setHistoryOpen(true)}
          tw={tw}
        />
      </SourceHoverCard>
    );
  } else {
    // Pending, no conflict. Unboxed: the value reads as plain text over the
    // row's pending tint, with the source tooltip on hover. The value text
    // itself is not the edit trigger here — the ✓ / ✗ controls are the
    // primary actions for unconfirmed rows.
    valueNode = (
      <SourceHoverCard
        triggerStyle={{ width: "100%" }}
        trigger={
          <ValueBox>
            <RestingValue
              value={displayValue}
              unit={unit}
              isMultiSelect={isMultiSelect}
              expanded={valueExpanded}
              onToggle={() => setValueExpanded((current) => !current)}
              showMoreLabel={tw.showMore}
              showLessLabel={tw.showLess}
            />
          </ValueBox>
        }
      >
        <TooltipSourceList
          label={label}
          displayValue={displayValue}
          unit={unit}
          sources={sources}
          currentValue={value}
          isMultiSelect={isMultiSelect}
          registryCode={registryCode}
          onDocPreview={onDocPreview}
          onOpenHistory={() => setHistoryOpen(true)}
          tw={tw}
        />
      </SourceHoverCard>
    );
  }

  // Actions share one bounded slot with the status marker. Pointer users see
  // them on hover; keyboard focus and non-hover devices reveal them as well.
  // Keeping both layers inside the same grid track prevents the review badge
  // from colliding with the controls without squeezing the value column.
  const showControls = hasFieldControls({
    canManage,
    editing,
    proposalActive,
    isSaving,
    state,
    valueOutsideEnum,
  });
  const controls = showControls ? (
    <FieldControls
      state={state}
      isSaving={isSaving}
      hasConflict={hasConflict}
      expanded={expanded}
      valueOutsideEnum={valueOutsideEnum}
      tw={tw}
      onConfirm={() => commitValue(value)}
      onEdit={() => setEditing(true)}
      onClear={clearValue}
      onExpand={() => setExpanded(true)}
      onDismiss={() => {
        setExpanded(false);
        setCustomValue("");
      }}
    />
  ) : null;
  const controlsVisibleAtRest = isSaving;
  // Rows the user still owes something: an unreviewed finding, or a saved value
  // a newer finding now disagrees with. Both wear the amber bar and tint — the
  // proposal is presentation only, `field.state` stays "confirmed" so the tab
  // counts, the review queue and "Confirm all" are untouched.
  const needsAttention = state === "pending" || proposalActive;

  return (
    <div
      className="group/field"
      data-bd-field={field.id}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => onSelect?.(field)}
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(120px, 200px) minmax(0, 1fr) minmax(104px, 136px)",
        alignItems: "start",
        columnGap: gap,
        padding: `${padV}px 10px ${padV}px 14px`,
        borderRadius: 6,
        borderLeft: needsAttention ? "2.5px solid hsl(var(--warning))" : "2.5px solid transparent",
        boxShadow: selected ? "inset 0 0 0 1px rgba(23, 37, 31, 0.16)" : "none",
        // Unboxed rows: no dividers or per-value borders — interactivity
        // reads through a subtle whole-row hover tint. Rows carrying review
        // work keep their tint.
        background: needsAttention
          ? "linear-gradient(90deg, hsl(var(--warning) / 0.12), hsl(var(--warning) / 0.03) 75%)"
          : hover
            ? "var(--bd-bg)"
            : "transparent",
        transition: "background 120ms",
        position: "relative",
        cursor: onSelect ? "pointer" : "default",
      }}
    >
      <div style={{ minWidth: 0 }}>
        {onSelect ? (
          <button
            type="button"
            aria-current={selected ? "true" : undefined}
            onClick={(event) => {
              event.stopPropagation();
              onSelect(field);
            }}
            className="block w-full border-0 bg-transparent p-0 text-left"
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "var(--bd-ink)",
              marginBottom: 3,
              letterSpacing: "0.005em",
            }}
          >
            {label}
          </button>
        ) : (
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "var(--bd-ink)",
              marginBottom: 3,
              letterSpacing: "0.005em",
            }}
          >
            {label}
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 2,
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 6,
            justifyContent: "flex-start",
            minWidth: 0,
          }}
        >
          <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>{valueNode}</div>
        </div>

        {/* A conflict names no source: the row offers "N options" instead, and
            crediting the first one here would pin the value on a registry the
            user has not chosen. Expanding lists every source per candidate. */}
        {reference && state !== "empty" && !(state === "pending" && hasConflict) ? (
          // w-fit: the strip swallows clicks, so it must not stretch across the
          // column — the space beside it belongs to the row's own onSelect.
          <span
            className="mt-1 flex w-fit max-w-full items-center gap-1.5"
            onClick={(event) => event.stopPropagation()}
          >
            {reference.kind === "ehr" ? (
              <EhrChip url={ehrUrlFor(registryCode)} className="h-5 px-1.5 text-[10px]" />
            ) : reference.kind === "registry" ? (
              // Another registry reported this — name it, never link it to EHR.
              <span className="min-w-0 truncate text-[11px] text-muted-foreground">
                {reference.filename}
              </span>
            ) : (
              <RowSourceDocs filenames={reference.filenames} onDocPreview={onDocPreview} />
            )}
          </span>
        ) : null}

        {pendingExtremeValue !== null && (
          <div
            role="alert"
            style={{
              marginTop: 6,
              padding: "8px 10px",
              border: "1px solid var(--bd-warn-soft)",
              background: "rgba(255, 200, 130, 0.1)",
              borderRadius: 6,
              display: "flex",
              flexDirection: "column",
              gap: 6,
              fontSize: 12,
              color: "var(--bd-ink)",
              textAlign: "left",
            }}
          >
            <div style={{ fontWeight: 600 }}>{tw.extremeValueTitle}</div>
            <div style={{ color: "var(--bd-ink-2, var(--bd-ink))" }}>{tw.extremeValueBody}</div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => {
                  setPendingExtremeValue(null);
                  setEditing(true);
                }}
                style={{
                  fontSize: 12,
                  padding: "4px 10px",
                  borderRadius: 4,
                  border: "1px solid var(--bd-line)",
                  background: "hsl(var(--background))",
                  cursor: "pointer",
                }}
              >
                {tw.extremeValueEdit}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (pendingExtremeValue !== null) persistValue(pendingExtremeValue);
                }}
                style={{
                  fontSize: 12,
                  padding: "4px 10px",
                  borderRadius: 4,
                  border: "1px solid var(--bd-warn)",
                  background: "var(--bd-warn)",
                  color: "hsl(var(--destructive-foreground))",
                  cursor: "pointer",
                }}
              >
                {tw.extremeValueSaveAnyway}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="relative flex min-h-7 min-w-0 items-center justify-end pt-1">
        <div
          className={
            controls
              ? controlsVisibleAtRest
                ? "opacity-0"
                : "transition-opacity group-focus-within/field:opacity-0 group-hover/field:opacity-0 [@media(hover:none)]:opacity-100"
              : undefined
          }
        >
          <StatusMarker
            state={state}
            label={
              proposalActive
                ? tw.statusNewFinding
                : state === "confirmed"
                  ? tw.statusConfirmed
                  : state === "pending"
                    ? tw.statusNeedsReview
                    : tw.statusMissing
            }
            proposal={
              proposalActive
                ? { expanded, onToggle: () => setExpanded((current) => !current) }
                : undefined
            }
          />
        </div>
        {controls ? (
          <div
            className={
              controlsVisibleAtRest
                ? "absolute right-0 top-0.5 flex items-center gap-0.5 rounded-md bg-background/95 px-0.5 py-0.5 opacity-100 shadow-sm"
                : "pointer-events-none absolute right-0 top-0.5 flex items-center gap-0.5 rounded-md bg-background/95 px-0.5 py-0.5 opacity-0 shadow-sm transition-opacity group-focus-within/field:pointer-events-auto group-focus-within/field:opacity-100 group-hover/field:pointer-events-auto group-hover/field:opacity-100 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:static [@media(hover:none)]:ml-1 [@media(hover:none)]:opacity-100"
            }
            style={{
              background: needsAttention ? "hsl(var(--warning) / 0.08)" : "hsl(var(--background))",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            {controls}
          </div>
        ) : null}
      </div>

      {state === "pending" && hasConflict && expanded && canManage && (
        <div
          style={{
            gridColumn: "1 / -1",
            marginTop: 8,
            paddingLeft: 12,
            borderLeft: "2px solid var(--bd-warn-soft)",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {valueOptions.map((option) => (
            <div
              key={option.value}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "flex-end",
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  textAlign: "right",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                <ValueText
                  value={
                    enumRule
                      ? (enumRule.options.find((o) => o.value === option.value)?.label ??
                        option.value)
                      : option.value
                  }
                  unit={unit}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 4,
                }}
              >
                {option.groups.map((g, idx) => (
                  <SourceGroupChip
                    key={`${g.kind}-${idx}`}
                    group={g}
                    registryCode={registryCode}
                    onDocPreview={onDocPreview}
                  />
                ))}
              </div>
              <IconButton
                onClick={() => commitValue(option.value)}
                title={tw.confirmThisValue}
                color="var(--bd-ok)"
              >
                <Check size={14} />
              </IconButton>
            </div>
          ))}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              justifyContent: "flex-end",
              marginTop: 4,
            }}
          >
            <input
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitValue(customValue);
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setExpanded(false);
                  setCustomValue("");
                }
              }}
              placeholder={tw.typeNewValue}
              style={{
                flex: 1,
                maxWidth: 220,
                fontSize: 13,
                color: "var(--bd-ink)",
                textAlign: "right",
                border: "1px solid var(--bd-line)",
                borderRadius: 4,
                padding: "2px 6px",
                outline: "none",
                background: "hsl(var(--background))",
              }}
            />
            <IconButton
              onClick={() => commitValue(customValue)}
              title={tw.saveTypedValue}
              color="var(--bd-ok)"
            >
              <Check size={14} />
            </IconButton>
          </div>
        </div>
      )}

      {proposalActive && proposedChange && expanded && canManage && (
        // WHA-3640: compact reveal for a newer extractor finding that disagrees
        // with the confirmed value. Mirrors the conflict resolver's layout —
        // current value on top, the new finding (with its source chip) below,
        // then keep / accept. Accept (commitValue) writes the new value; keep
        // (persistValue of the current value) re-stamps user_data so the
        // proposal stops resurfacing on the next refetch (WHA-3291).
        <div
          style={{
            gridColumn: "1 / -1",
            marginTop: 8,
            paddingLeft: 12,
            borderLeft: "2px solid var(--bd-warn-soft)",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              justifyContent: "flex-end",
            }}
          >
            <span
              style={{
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                fontSize: 11,
                color: "var(--bd-ink-4)",
              }}
            >
              {tw.currentValueLabel}
            </span>
            <ValueText value={displayValue} unit={unit} />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              justifyContent: "flex-end",
            }}
          >
            <span
              style={{
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                fontSize: 11,
                color: "var(--bd-warn)",
              }}
            >
              {tw.proposedValueLabel}
            </span>
            <ValueText value={proposedDisplayValue} unit={unit} />
            <SourceGroupChip
              group={{
                kind: "extractor",
                value: proposedChange.value,
                filenames: proposedChange.filenames,
                reasoning: proposedChange.reasoning,
              }}
              registryCode={registryCode}
              onDocPreview={onDocPreview}
            />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              justifyContent: "flex-end",
              marginTop: 2,
            }}
          >
            <button
              type="button"
              onClick={() => persistValue(value)}
              style={{
                fontSize: 12,
                padding: "4px 10px",
                borderRadius: 4,
                border: "1px solid var(--bd-line)",
                background: "hsl(var(--background))",
                color: "var(--bd-ink)",
                cursor: "pointer",
              }}
            >
              {tw.keepCurrentValue}
            </button>
            <button
              type="button"
              onClick={() => commitValue(proposedChange.value)}
              style={{
                fontSize: 12,
                padding: "4px 10px",
                borderRadius: 4,
                border: "1px solid var(--bd-warn)",
                background: "var(--bd-warn)",
                color: "hsl(var(--destructive-foreground))",
                cursor: "pointer",
              }}
            >
              {tw.acceptNewValue}
            </button>
          </div>
        </div>
      )}

      <AttributeHistoryModal
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        field={field}
        buildingId={buildingId}
        orgId={orgId}
        registryCode={registryCode}
        onDocPreview={onDocPreview}
      />
    </div>
  );
}
